import React from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { Spotlight, SpotlightTransition } from '@atlaskit/onboarding';
import { N0 } from '@atlaskit/theme/colors';
import { token, useThemeObserver } from '@atlaskit/tokens';
import { UNSAFE_LAYERING } from '@atlaskit/layering';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import nav4ChangeboardingSpotlightImage from './assets/nav4ChangeboardingSpotlightImage.svg';
import nav4ChangeboardingSpotlightImageDark from './assets/nav4ChangeboardingSpotlightImageDark.svg';

export const NAV_4_CHANGEBOARDING_ENGAGEMENT_PLATFORM_MESSAGE_KEY = 'cc-nav-4-changeboarding';

export interface Nav4ChangeboardingSpotlightProps {
	onClose?: () => void;
	activeSpotlight: number | null;
	setActiveSpotlight: (activeSpotlight: number | null) => void;
}

export const Nav4ChangeboardingSpotlight = ({
	onClose,
	activeSpotlight,
	setActiveSpotlight,
}: Nav4ChangeboardingSpotlightProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';

	const next = () => setActiveSpotlight((activeSpotlight || 0) + 1);
	const back = () => setActiveSpotlight((activeSpotlight || 1) - 1);
	const end = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'nav4ChangeboardingSpotlight',
				source: 'confluenceAtlassianOnboardingNav4',
			},
		}).fire();

		onClose?.();
		setActiveSpotlight(null);
	};

	const renderActiveSpotlight = () => {
		const spotlights = [
			<Spotlight
				key="nav4-spotlight-1"
				target="nav4-spotlight-1"
				image={isDarkMode ? nav4ChangeboardingSpotlightImageDark : nav4ChangeboardingSpotlightImage}
				heading={intl.formatMessage(i18n.changeboardingSpotlight1Heading)}
				dialogPlacement="right top"
				dialogWidth={338}
				actionsBeforeElement="1/2"
				actions={[
					{
						onClick: () => next(),
						text: intl.formatMessage(i18n.changeboardingNextButton),
					},
					{
						onClick: () => end(),
						text: intl.formatMessage(i18n.changeboardingSkipButton),
					},
				]}
				targetRadius={3}
				targetBgColor={token('elevation.surface', N0)}
			>
				<FormattedMessage
					{...i18n.changeboardingSpotlight1Text}
					values={{
						// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
						strong: (chunks: React.ReactNode[]) => <strong>{chunks}</strong>,
					}}
				/>
			</Spotlight>,
			<Spotlight
				key="nav4-spotlight-2"
				target="nav4-spotlight-2"
				heading={intl.formatMessage(i18n.changeboardingSpotlight2Heading)}
				dialogPlacement="right top"
				dialogWidth={338}
				actionsBeforeElement="2/2"
				actions={[
					{ onClick: () => end(), text: intl.formatMessage(i18n.changeboardingDismissButton) },
					{
						onClick: () => back(),
						text: 'Back',
					},
				]}
				targetRadius={3}
				targetBgColor={token('elevation.surface', N0)}
			>
				{intl.formatMessage(i18n.changeboardingSpotlight2Text)}
			</Spotlight>,
		];

		if (activeSpotlight === null) {
			return null;
		}

		return spotlights[activeSpotlight];
	};

	return (
		<SpotlightTransition>
			<UNSAFE_LAYERING isDisabled={false}>{renderActiveSpotlight()}</UNSAFE_LAYERING>
		</SpotlightTransition>
	);
};

const i18n = defineMessages({
	changeboardingSpotlight1Heading: {
		id: 'nav4-onboarding.changeboarding-spotlight-1.heading',
		defaultMessage: 'Quick access to settings and more',
		description: 'Heading for the first nav 4 changeboarding spotlight',
	},
	changeboardingSpotlight1Text: {
		id: 'nav4-onboarding.changeboarding-spotlight-1.text',
		defaultMessage:
			'Find <strong>Space settings</strong> in <strong>More actions</strong> (<strong>•••</strong>), where you can manage permissions, access, and content, and access Premium features.',
		description: 'Text for the first nav 4 changeboarding spotlight',
	},
	changeboardingSpotlight2Heading: {
		id: 'nav4-onboarding.changeboarding-spotlight-2.heading',
		defaultMessage: 'Content comes first',
		description: 'Heading for the second nav 4 changeboarding spotlight',
	},
	changeboardingSpotlight2Text: {
		id: 'nav4-onboarding.changeboarding-spotlight-2.text',
		defaultMessage:
			'We’ve moved Calendar, Questions, and Apps below your content so you can focus on your most important tasks.',
		description: 'Text for the second nav 4 changeboarding spotlight',
	},
	changeboardingSkipButton: {
		id: 'nav4-onboarding.changeboarding-spotlight.skip',
		defaultMessage: 'Skip',
		description: 'Text on the button to click to indicate you want to skip the spotlight',
	},
	changeboardingNextButton: {
		id: 'nav4-onboarding.changeboarding-spotlight.next',
		defaultMessage: 'Next',
		description: 'Text on the button to click to indicate you want to see the next spotlight',
	},
	changeboardingDismissButton: {
		id: 'nav4-onboarding.changeboarding-spotlight.dismiss',
		defaultMessage: 'Done',
		description: 'Text on the button to click to indicate you are done viewing the spotlight',
	},
});
